/**
 * Sidebar Component
 * Enterprise-grade navigation sidebar with collapsible sections
 * organized by business domain.
 */

import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useState, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { 
  ChevronDown, 
  ChevronRight, 
  List, 
  Plus, 
  Clock,
  Search,
  LogOut,
  Calendar as CalendarIcon,
  Lock,
  FolderKanban,
  TrendingUp
} from 'lucide-react'
import { useAuthStore } from '../stores/authStore'
import { usePermission, PermissionGate } from '../hooks/usePermission'
import { useNavigation, getAdditionalNavigation } from '../hooks/useNavigation'
import { SIDEBAR_THEMES, DEFAULT_SIDEBAR_THEME } from '../theme/sidebarThemes'
import { useSidebarThemeStore } from '../stores/sidebarThemeStore'
import { ONDERBALK_HOOGTE_PX } from '../theme/onderbalk'
import { useHasManagementAccess } from '../hooks/useHasManagementAccess'
import { useFeature } from '../hooks/useFeature'
import { MODULE_SKETCHUP, MODULE_VECTORWORKS, MODULE_B2B_PROJECTS } from '../constants/modules'
import api from '../services/api'
import GlobalSearch from './GlobalSearch'
import Logo from './Logo'
import CalendarLinkModal from './CalendarLinkModal'
import ProjectInitiationDialog from './ProjectInitiationDialog'
import UpgradeModal from './UpgradeModal'

function Sidebar() {
  const { user, logout } = useAuthStore()
  const { t } = useTranslation()
  const location = useLocation()
  const navigate = useNavigate()
  // BLOK 4: sidebar-thema komt nu uit de gebruikersvoorkeur (sidebarThemeStore, localStorage), niet meer
  // vast op de default. `sb` levert alle themed class-fragmenten (geen losse kleuren in de JSX).
  const actiefThema = useSidebarThemeStore((s) => s.actiefThema)
  const sb = SIDEBAR_THEMES[actiefThema] || SIDEBAR_THEMES[DEFAULT_SIDEBAR_THEME]
  const [showSearch, setShowSearch] = useState(false)
  const [showCalendarModal, setShowCalendarModal] = useState(false)
  const [projectenMenuOpen, setProjectenMenuOpen] = useState(false)
  const [upgradeModalModule, setUpgradeModalModule] = useState(null)
  
  // Feature checks
  const hasSketchUp = useFeature(MODULE_SKETCHUP)
  const hasVectorworks = useFeature(MODULE_VECTORWORKS)
  const hasB2BProjects = useFeature(MODULE_B2B_PROJECTS)
  const { hasManagementAccess } = useHasManagementAccess()
  
  // Track which navigation sections are expanded (standaard ingeklapt bij openen)
  const [expandedSections, setExpandedSections] = useState({
    home: true,
    sales: false,
    production: false,
    finance: false,
    'master-dna': false,
    professional: false,
    settings: false
  })
  
  // Get navigation - useNavigation is a hook, so call it directly
  // It's already memoized internally based on t
  const navigationGroups = useNavigation(t) || []
  const additionalNav = getAdditionalNavigation(t) || []

  // SuperAdmin: alleen de rolvlag is_superuser (#647)
  const isSuperAdmin = Boolean(user?.is_superuser)
  
  // Load recent projects (graceful fallback bij 500/auth)
  const { data: recentProjecten } = useQuery({
    queryKey: ['projecten', user?.id],
    queryFn: async () => {
      try {
        const response = await api.get('/projecten')
        return response.data || []
      } catch {
        return []
      }
    },
    select: (data) => {
      if (!Array.isArray(data)) return []
      return data
        .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0))
        .slice(0, 3)
    },
    enabled: !!user?.id,
    retry: false,
    staleTime: 60000,
  })
  
  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }))
  }
  
  const formatProjectId = (id) => {
    return 'PRJ-' + id.toString().padStart(4, '0')
  }
  
  const [showProjectInitDialog, setShowProjectInitDialog] = useState(false)

  const handleNewProject = () => {
    setShowProjectInitDialog(true)
  }
  
  const isRouteActive = (href) => {
    if (href === '/dashboard') {
      return location.pathname === '/dashboard'
    }
    return location.pathname.startsWith(href)
  }
  
  const isSectionActive = (items) => {
    return items.some(item => isRouteActive(item.href))
  }
  
  // Check if item requires a feature module
  const getRequiredModule = (href, item) => {
    if (href === '/import/sketchup') return MODULE_SKETCHUP
    if (item?.requiresModule) return item.requiresModule
    return null
  }
  
  const handleLockedFeatureClick = (e, moduleName) => {
    e.preventDefault()
    setUpgradeModalModule(moduleName)
  }
  
  // Render navigation item (without permission check - use PermissionGate wrapper)
  const renderNavItem = (item, level = 0) => {
    const Icon = item.icon
    const isActive = isRouteActive(item.href)
    const requiredModule = getRequiredModule(item.href, item)
    const hasFeature = requiredModule 
      ? (requiredModule === MODULE_SKETCHUP ? hasSketchUp : 
         requiredModule === MODULE_VECTORWORKS ? hasVectorworks : 
         requiredModule === MODULE_B2B_PROJECTS ? hasB2BProjects : true)
      : true
    
    // Special handling for projecten expandable menu
    if (item.isExpandable && item.href === '/projecten') {
      return (
        <div key={item.href} className="mb-1">
          <button
            onClick={() => setProjectenMenuOpen(!projectenMenuOpen)}
            className={`w-full flex items-center justify-between px-4 py-2 rounded-lg transition-colors ${
              location.pathname.startsWith('/projecten') ? sb.itemActive : sb.itemIdle
            }`}
            style={{ paddingLeft: `${2 + level * 0.75}rem` }}
          >
            <div className="flex items-center">
              <Icon className="w-5 h-5 mr-3" />
              <span>{item.name}</span>
            </div>
            {projectenMenuOpen ? (
              <ChevronDown className="w-4 h-4" />
            ) : (
              <ChevronRight className="w-4 h-4" />
            )}
          </button>
          
          {projectenMenuOpen && (
            <div className="ml-4 mt-1 space-y-1">
              <Link
                to="/projecten"
                className={`flex items-center px-4 py-2 rounded-lg transition-colors text-sm ${
                  location.pathname === '/projecten' ? sb.subActive : sb.subIdle
                }`}
              >
                <List className="w-4 h-4 mr-2" />
                {t('layout.navigation.projectenMenu.all', { defaultValue: 'Alle Dossiers' })}
              </Link>
              <Link
                to="/projecten-overzicht"
                className={`flex items-center px-4 py-2 rounded-lg transition-colors text-sm ${
                  location.pathname === '/projecten-overzicht' ? sb.subActive : sb.subIdle
                }`}
              >
                <FolderKanban className="w-4 h-4 mr-2" />
                {t('layout.navigation.projectenMenu.overzicht')}
              </Link>
              <Link
                to="/projecten-dashboard"
                className={`flex items-center px-4 py-2 rounded-lg transition-colors text-sm ${
                  location.pathname === '/projecten-dashboard' ? sb.subActive : sb.subIdle
                }`}
              >
                <TrendingUp className="w-4 h-4 mr-2" />
                {t('layout.navigation.projectenMenu.dashboard')}
              </Link>
              <button
                onClick={handleNewProject}
                className={`w-full flex items-center px-4 py-2 rounded-lg transition-colors text-sm ${sb.subIdle}`}
              >
                <Plus className="w-4 h-4 mr-2" />
                {t('layout.navigation.projectenMenu.nieuwDossier')}
              </button>
              {recentProjecten && recentProjecten.length > 0 && (
                <>
                  <div className={`px-4 py-1 text-xs font-semibold uppercase tracking-wider mt-2 ${sb.subLabel}`}>
                    {t('layout.navigation.projectenMenu.recent', { defaultValue: 'Recent' })}
                  </div>
                  {recentProjecten.map((project) => (
                    <Link
                      key={project.id}
                      to={`/projecten/${project.id}/serviceoverzicht`}
                      className={`flex items-center justify-between px-4 py-2 rounded-lg transition-colors text-sm group ${sb.subIdle}`}
                      title={`${formatProjectId(project.id)} - ${project.name}`}
                    >
                      <div className="flex items-center min-w-0 flex-1">
                        <Clock className="w-4 h-4 mr-2 flex-shrink-0" />
                        <span className="truncate">{project.name}</span>
                      </div>
                      <span className="ml-2 text-xs font-mono font-semibold text-gray-400 dark:text-gray-500 group-hover:text-gray-600 dark:group-hover:text-gray-300 flex-shrink-0">
                        {formatProjectId(project.id)}
                      </span>
                    </Link>
                  ))}
                </>
              )}
            </div>
          )}
        </div>
      )
    }
    
    // If feature is locked, render as button with lock icon
    if (requiredModule && !hasFeature) {
      return (
        <button
          key={item.href}
          onClick={(e) => handleLockedFeatureClick(e, requiredModule)}
          className={`flex items-center justify-between w-full px-4 py-2 rounded-lg transition-colors ${sb.itemLocked}`}
          style={{ paddingLeft: `${2 + level * 0.75}rem` }}
          title={t('layout.navigation.moduleNotActivated')}
        >
          <div className="flex items-center">
            <Icon className="w-5 h-5 mr-3 flex-shrink-0" />
            <span className="truncate">{item.name}</span>
          </div>
          <Lock className="w-4 h-4 flex-shrink-0 ml-2" />
        </button>
      )
    }
    
    return (
      <Link
        key={item.href}
        to={item.href}
        className={`flex items-center px-4 py-2 rounded-lg transition-colors ${isActive ? sb.itemActive : sb.itemIdle}`}
        style={{ paddingLeft: `${2 + level * 0.75}rem` }}
      >
        <Icon className="w-5 h-5 mr-3 flex-shrink-0" />
        <span className="truncate">{item.name}</span>
      </Link>
    )
  }
  
  return (
    <>
      <GlobalSearch isOpen={showSearch} onClose={() => setShowSearch(false)} onToggle={() => setShowSearch(prev => !prev)} />
      <UpgradeModal 
        isOpen={!!upgradeModalModule} 
        onClose={() => setUpgradeModalModule(null)}
        moduleName={upgradeModalModule || ''}
      />
      
      <div className={`fixed top-0 left-0 w-64 z-30 ${sb.root}`} style={{ bottom: ONDERBALK_HOOGTE_PX }}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className={`flex items-center justify-between px-4 h-16 border-b ${sb.divider}`}>
            <div className="flex items-center gap-2.5">
              <Logo size={32} />
              <div className="leading-none">
                <span className={`text-xl font-bold tracking-tight ${sb.brand}`}>Orion</span>
                <span className={`ml-1 text-[0.65rem] font-bold align-top uppercase tracking-wider ${sb.brandAccent}`}>SaaS</span>
              </div>
            </div>
          </div>

          {/* Global Search Button */}
          <div className={`px-4 py-3 border-b ${sb.divider}`}>
            <button
              onClick={() => setShowSearch(true)}
              className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg transition-colors text-sm ${sb.searchBtn}`}
            >
              <Search className="w-4 h-4" />
              <span>{t('common.search')}</span>
              <kbd className={`ml-auto px-2 py-0.5 rounded text-xs ${sb.kbd}`}>
                ⌘K
              </kbd>
            </button>
          </div>
          
          {/* Navigation Groups */}
          <nav className="flex-1 px-2 py-4 space-y-1 overflow-y-auto">
            {navigationGroups && navigationGroups.length > 0 && navigationGroups.map((group) => {
              const GroupIcon = group.icon
              const isExpanded = expandedSections[group.id]
              const hasActiveItem = isSectionActive(group.items)

              // Filter items op zichtbaarheid (superadmin, management, role checks)
              const visibleItems = group.items
                .filter((item) => !item.requiresSuperAdmin || isSuperAdmin)
                .filter((item) => {
                  if (item.requiresManagement) return hasManagementAccess
                  return true
                })
                .filter((item) => {
                  if (!item.requiresRole) return true
                  const userRole = String(user?.role || '').toUpperCase()
                  const isAdmin = user?.is_admin || userRole === 'ADMIN'
                  if (isAdmin) return true
                  return item.requiresRole.some((r) => String(r).toUpperCase() === userRole)
                })

              // Verberg hele sectie als er geen zichtbare items zijn
              if (visibleItems.length === 0) return null

              return (
                <div key={group.id} className="mb-2">
                  {/* Section Header */}
                  <button
                    onClick={() => toggleSection(group.id)}
                    className={`w-full flex items-center justify-between px-3 py-2 rounded-lg transition-colors text-sm font-semibold uppercase tracking-wider ${hasActiveItem ? sb.groupActive : sb.groupIdle}`}
                  >
                    <div className="flex items-center">
                      <GroupIcon className="w-4 h-4 mr-2" />
                      <span>{group.label}</span>
                    </div>
                    {isExpanded ? (
                      <ChevronDown className="w-4 h-4" />
                    ) : (
                      <ChevronRight className="w-4 h-4" />
                    )}
                  </button>

                  {/* Section Items */}
                  {isExpanded && (
                    <div className="mt-1 space-y-1">
                      {visibleItems.map((item) => (
                          <PermissionGate key={item.href} resource={item.resource} action={item.action}>
                            {renderNavItem(item)}
                          </PermissionGate>
                        ))}
                    </div>
                  )}
                </div>
              )
            })}
            
            {/* Additional Navigation (Documents) */}
            {additionalNav && additionalNav.length > 0 && additionalNav.map((item) => {
              const Icon = item.icon
              const isActive = isRouteActive(item.href)
              
              return (
                <PermissionGate key={item.href} resource={item.resource} action={item.action}>
                  <Link
                    to={item.href}
                    className={`flex items-center px-4 py-2 rounded-lg transition-colors ${isActive ? sb.itemActive : sb.itemIdle}`}
                  >
                    <Icon className="w-5 h-5 mr-3" />
                    {item.name}
                  </Link>
                </PermissionGate>
              )
            })}
          </nav>
          
          {/* User Profile & Actions */}
          <div className={`border-t p-4 space-y-2 ${sb.divider}`}>
            <div className="mb-2">
              <p className={`text-sm font-medium ${sb.userName}`}>{user?.gebruikersnaam}</p>
              <p className={`text-xs ${sb.userMeta}`}>
                {user?.rol && (
                  <span className={`px-2 py-0.5 rounded text-xs font-semibold mr-2 ${sb.badge}`}>
                    {user.rol}
                  </span>
                )}
                {t('layout.user.tenant')}: {user?.tenant_id || 'N/A'}
              </p>
            </div>
            <button
              onClick={() => setShowCalendarModal(true)}
              className={`flex items-center w-full px-4 py-2 text-sm rounded-lg transition-colors ${sb.footerBtn}`}
            >
              <CalendarIcon className="w-4 h-4 mr-2" />
              {t('calendar.title', { defaultValue: 'Agenda Link' })}
            </button>
            <button
              onClick={logout}
              className={`flex items-center w-full px-4 py-2 text-sm rounded-lg transition-colors ${sb.footerBtn}`}
            >
              <LogOut className="w-4 h-4 mr-2" />
              {t('layout.user.logout')}
            </button>
          </div>
        </div>
      </div>
      
      <CalendarLinkModal 
        isOpen={showCalendarModal} 
        onClose={() => setShowCalendarModal(false)} 
      />
      
      <ProjectInitiationDialog
        isOpen={showProjectInitDialog}
        onClose={() => setShowProjectInitDialog(false)}
      />
    </>
  )
}

export default Sidebar
